import React, { useState, useEffect } from 'react';
import {
  Investigation,
  InvestigationStatus,
  InvestigationPriority,
  InvestigationType,
  InvestigationDashboardStats,
} from './types';
import { investigationService } from './investigationService';
import { InvestigationsTable } from './components/InvestigationsTable';
import { InvestigationWorkspaceModal } from './components/InvestigationWorkspaceModal';
import { CreateInvestigationModal } from './components/CreateInvestigationModal';
import { InvestigationTemplatesTab } from './components/InvestigationTemplatesTab';

type SubmenuView = 'all' | 'open' | 'pending_review' | 'closed' | 'templates';

export const InvestigationsPage: React.FC = () => {
  const [currentView, setCurrentView] = useState<SubmenuView>('all');
  const [investigations, setInvestigations] = useState<Investigation[]>([]);
  const [stats, setStats] = useState<InvestigationDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPriority, setSelectedPriority] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedInvestigation, setSelectedInvestigation] = useState<Investigation | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchStats = async () => {
    try {
      const data = await investigationService.getDashboardStats();
      setStats(data);
    } catch (err: any) {
      console.error('Failed to load investigation stats:', err);
    }
  };

  const fetchInvestigations = async () => {
    if (currentView === 'templates') return;

    try {
      setLoading(true);
      let statusFilter: InvestigationStatus[] | undefined = undefined;

      if (currentView === 'open') {
        statusFilter = [
          'DRAFT',
          'INITIATED',
          'INVESTIGATOR_ASSIGNED',
          'EVIDENCE_COLLECTION',
          'ROOT_CAUSE_ANALYSIS',
          'DRAFT_REPORT',
          'PENDING_ACTIONS',
          'REOPENED',
        ];
      } else if (currentView === 'pending_review') {
        statusFilter = ['PENDING_REVIEW', 'APPROVED'];
      } else if (currentView === 'closed') {
        statusFilter = ['CLOSED'];
      }

      const res = await investigationService.getInvestigations({
        status: statusFilter,
        priority: selectedPriority ? (selectedPriority as InvestigationPriority) : undefined,
        type: selectedType ? (selectedType as InvestigationType) : undefined,
        searchTerm: searchTerm.trim() || undefined,
      });

      setInvestigations(res.items || []);
    } catch (err: any) {
      console.error('Failed to load investigations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    const handleUrlSelect = () => {
      const params = new URLSearchParams(window.location.search);
      const targetId = params.get('investigationId') || params.get('id');
      const targetNumber = params.get('investigationNumber') || params.get('invNumber');

      if (targetId) {
        investigationService
          .getInvestigationById(targetId)
          .then((inv) => {
            if (inv) {
              setSelectedInvestigation(inv);
            } else if (targetNumber) {
              investigationService.getInvestigations({ searchTerm: targetNumber }).then((res) => {
                const found = res.items?.find((i) => i.investigationNumber === targetNumber) || res.items?.[0];
                if (found) setSelectedInvestigation(found);
              }).catch(() => {});
            }
          })
          .catch((err) => {
            console.warn('Could not open investigation by ID from URL:', err);
            if (targetNumber) {
              investigationService.getInvestigations({ searchTerm: targetNumber }).then((res) => {
                const found = res.items?.find((i) => i.investigationNumber === targetNumber) || res.items?.[0];
                if (found) setSelectedInvestigation(found);
              }).catch(() => {});
            }
          });
      } else if (targetNumber) {
        investigationService
          .getInvestigationById(targetNumber)
          .then((inv) => {
            if (inv) {
              setSelectedInvestigation(inv);
            } else {
              investigationService.getInvestigations({ searchTerm: targetNumber }).then((res) => {
                const found = res.items?.find((i) => i.investigationNumber === targetNumber) || res.items?.[0];
                if (found) setSelectedInvestigation(found);
              }).catch(() => {});
            }
          })
          .catch(() => {
            investigationService.getInvestigations({ searchTerm: targetNumber }).then((res) => {
              const found = res.items?.find((i) => i.investigationNumber === targetNumber) || res.items?.[0];
              if (found) setSelectedInvestigation(found);
            }).catch(() => {});
          });
      }
    };

    handleUrlSelect();
    window.addEventListener('popstate', handleUrlSelect);
    return () => window.removeEventListener('popstate', handleUrlSelect);
  }, []);

  useEffect(() => {
    fetchInvestigations();
  }, [currentView, selectedPriority, selectedType]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchInvestigations();
  };

  return (
    <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 24px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Top Header */}
      <div className="page-header-banner">
        <div className="badge">SAFETY MANAGEMENT</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Safety Investigations</h1>
            <p>ICAO Annex 13 & EASA Part-IS causal analysis (5-Why, Ishikawa 6M, HFACS) and evidence custody dossiers.</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
            style={{ width: 'auto', padding: '10px 18px' }}
          >
            + Launch Investigation
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Dossiers</div>
            <div className="stat-value">{stats?.totalInvestigations ?? '-'}</div>
            <div className="stat-sub">Investigation register</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">In Progress / Active</div>
            <div className="stat-value" style={{ color: '#0284c7' }}>{stats?.openCount ?? '-'}</div>
            <div className="stat-sub">Field analysis</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Pending Safety Review</div>
            <div className="stat-value" style={{ color: '#9333ea' }}>{stats?.pendingReviewCount ?? '-'}</div>
            <div className="stat-sub">Board signoff</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Closed & Approved</div>
            <div className="stat-value" style={{ color: '#16a34a' }}>{stats?.closedCount ?? '-'}</div>
            <div className="stat-sub">Final report published</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">SLA Overdue / At Risk</div>
            <div className="stat-value" style={{ color: '#dc2626' }}>{stats?.overdueCount ?? 0}</div>
            <div className="stat-sub">Exceeded statutory window</div>
          </div>
        </div>
      )}

      {/* Submenu Navigation */}
      <div className="nav-tabs">
        {[
          { id: 'all', label: 'All Investigations' },
          { id: 'open', label: 'In Progress & Active' },
          { id: 'pending_review', label: 'Pending Safety Review' },
          { id: 'closed', label: 'Closed & Archived' },
          { id: 'templates', label: 'Frameworks & Templates' },
        ].map((sub) => (
          <button
            key={sub.id}
            onClick={() => setCurrentView(sub.id as SubmenuView)}
            className={`tab-btn ${currentView === sub.id ? 'active' : ''}`}
          >
            {sub.label}
          </button>
        ))}
      </div>

      {/* Filter and Search Bar (for investigation list views) */}
      {currentView !== 'templates' && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '8px', flex: 1, maxWidth: '480px' }}>
            <input
              type="text"
              placeholder="Search by case number, title, or lead investigator..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
            />
            <button
              type="submit"
              style={{ background: '#334155', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
            >
              Search
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Type:</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
              >
                <option value="">All Types</option>
                <option value="OCCURRENCE">OCCURRENCE</option>
                <option value="AUDIT_FINDING">AUDIT FINDING</option>
                <option value="HAZARD">HAZARD</option>
                <option value="WHISTLEBLOWER">WHISTLEBLOWER</option>
                <option value="SPECIAL_REQUEST">SPECIAL REQUEST</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Priority:</label>
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
              >
                <option value="">All Priorities</option>
                <option value="CRITICAL">CRITICAL</option>
                <option value="HIGH">HIGH</option>
                <option value="MEDIUM">MEDIUM</option>
                <option value="LOW">LOW</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Main Tab Content */}
      {currentView === 'templates' ? (
        <InvestigationTemplatesTab />
      ) : (
        <InvestigationsTable
          investigations={investigations}
          loading={loading}
          onSelect={(inv) => setSelectedInvestigation(inv)}
          onRefresh={() => {
            fetchInvestigations();
            fetchStats();
          }}
        />
      )}

      {/* Modals */}
      {selectedInvestigation && (
        <InvestigationWorkspaceModal
          investigationId={selectedInvestigation.id}
          onClose={() => {
            setSelectedInvestigation(null);
            const params = new URLSearchParams(window.location.search);
            if (params.has('investigationId') || params.has('investigationNumber') || params.has('invNumber')) {
              params.delete('investigationId');
              params.delete('investigationNumber');
              params.delete('invNumber');
              const newUrl = `${window.location.pathname}?${params.toString()}`;
              window.history.replaceState({}, '', newUrl);
            }
          }}
          onUpdated={() => {
            fetchInvestigations();
            fetchStats();
          }}
        />
      )}

      {showCreateModal && (
        <CreateInvestigationModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchInvestigations();
            fetchStats();
          }}
        />
      )}
    </div>
  );
};
